import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { DocumentsCard } from '../components/Documents.tsx';
import { ArchiveFilesCard } from '../components/ArchiveFiles.tsx';
import { PersonPacketsCard } from '../components/PersonPacket.tsx';
import { CoverSheetTopBar, ScanPilesCard } from './CoverSheets.tsx';
import { ErrorBox } from '../components/Layout.tsx';
import type { VisitType } from '../components/Intake.tsx';
import { NewVisitChooser } from '../components/VisitChooser.tsx';
import { api, get, isAdmin, isStaff, patch, post } from '../lib/api.ts';
import { useAuth } from '../lib/auth.tsx';
import { useData } from '../lib/useData.ts';

/** Today in the browser's time zone (toISOString would give the UTC date). */
export const localToday = () => new Date(Date.now() - new Date().getTimezoneOffset() * 60_000).toISOString().slice(0, 10);

export interface Department {
  id: string; name: string; legacyPrefix: string | null; portalEnabled: boolean; portalPackets: boolean; participants: number;
  archivedAt?: string | null; archivedReason?: string | null;
  details?: OrgDetails;
}
export interface OrgDetails { category: string | null; state: string | null; county: string | null; street: string | null; city: string | null; zip: string | null; phone: string | null; website: string | null; lookupConfidence: string | null; lookupSource: string | null }
export interface ParticipantRow {
  id: string; firstName: string; lastName: string; middleName: string | null; dob: string | null; sex: 'M' | 'F' | null;
  employeeId: string | null; departmentId: string | null; departmentName: string | null; email: string | null;
  phone: string | null; street?: string | null; city?: string | null; state?: string | null; zip?: string | null; active: boolean; lastAssessedOn?: string | null; reviewFlags: string[];
  insuranceLast4?: string | null;   // the insurance ID itself is stored encrypted and never sent back
}

export function ParticipantList() {
  const { me } = useAuth();
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const dept = params.get('dept') ?? '';
  const [text, setText] = useState(q);
  const url = `/api/participants?limit=100${q ? `&q=${encodeURIComponent(q)}` : ''}${dept ? `&departmentId=${dept}` : ''}`;
  const { data, error, loading } = useData<ParticipantRow[]>(url);
  const depts = useData<Department[]>('/api/departments');
  const nav = useNavigate();
  const staff = me && isStaff(me.role);
  const [creating, setCreating] = useState(false);

  return (
    <>
      <div className="spread">
        <h1>{staff ? 'Participants' : 'Roster'}</h1>
        <div className="actions">{me && isAdmin(me.role) && <Link className="button-link" to="/admin/duplicates">Possible duplicates</Link>}
        {staff && <button onClick={() => setCreating(true)}>New participant</button>}</div>
      </div>
      {creating && <ParticipantForm departments={depts.data ?? []} onCancel={() => setCreating(false)}
        onSaved={(id) => nav(`/participants/${id}`)} />}
      <div className="card">
        <form className="row" onSubmit={(e) => { e.preventDefault(); setParams({ ...(text ? { q: text } : {}), ...(dept ? { dept } : {}) }); }}>
          <div><label htmlFor="q">Search name or employee ID</label>
            <input id="q" value={text} onChange={(e) => setText(e.target.value)} placeholder="e.g. Smith or 1326007" /></div>
          <div><label htmlFor="d">Organization</label>
            <select id="d" value={dept} onChange={(e) => setParams({ ...(q ? { q } : {}), ...(e.target.value ? { dept: e.target.value } : {}) })}>
              <option value="">All organizations</option>
              {depts.data?.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select></div>
          <div style={{ flex: '0 0 auto' }}><button>Search</button></div>
        </form>
      </div>
      <ErrorBox error={error} />
      <div className="card">
        {loading ? <p className="muted">Loading…</p> : !data?.length ? <p className="muted">No participants found.</p> : (
          <table>
            <thead><tr><th>Name</th><th>Employee ID</th><th>DOB</th><th>Sex</th><th>Organization</th><th>Last assessed</th></tr></thead>
            <tbody>
              {data.map((p) => (
                <tr key={p.id} className={staff ? 'link' : ''} onClick={() => staff && nav(`/participants/${p.id}`)}>
                  <td><strong>{p.lastName}, {p.firstName}</strong>{!p.active && <span className="badge" style={{ marginLeft: 6 }}>inactive</span>}</td>
                  <td>{p.employeeId ?? '—'}</td><td>{p.dob ?? '—'}</td><td>{p.sex ?? '—'}</td>
                  <td>{p.departmentName ?? <span className="muted">Unassigned</span>}</td>
                  <td>{p.lastAssessedOn ? String(p.lastAssessedOn).slice(0, 10) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

function ParticipantForm({ departments, initial, onSaved, onCancel }: {
  departments: Department[]; initial?: ParticipantRow; onSaved: (id: string) => void; onCancel: () => void;
}) {
  const [f, setF] = useState({
    firstName: initial?.firstName ?? '', middleName: initial?.middleName ?? '', lastName: initial?.lastName ?? '',
    dob: initial?.dob ?? '', sex: initial?.sex ?? '', employeeId: initial?.employeeId ?? '',
    departmentId: initial?.departmentId ?? '', email: initial?.email ?? '', phone: initial?.phone ?? '', insuranceId: '',
    street: initial?.street ?? '', city: initial?.city ?? '', state: initial?.state ?? '', zip: initial?.zip ?? '',
  });
  const [error, setError] = useState<unknown>(null);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const body = {
      firstName: f.firstName, lastName: f.lastName, middleName: f.middleName || null, dob: f.dob || null,
      sex: f.sex || null, employeeId: f.employeeId || null, departmentId: f.departmentId || null,
      email: f.email || null, phone: f.phone || null,
      street: f.street.trim() || null, city: f.city.trim() || null, state: f.state.trim() || null, zip: f.zip.trim() || null,
      // Blank keeps the one on file (it is never shown in full).
      ...(f.insuranceId.trim() ? { insuranceId: f.insuranceId.trim() } : {}),
    };
    try {
      if (initial) {
        await patch(`/api/participants/${initial.id}`, body);
        onSaved(initial.id);
      } else {
        const r = await post<{ id: string }>('/api/participants', body);
        onSaved(r.id);
      }
    } catch (err) { setError(err); }
  };
  return (
    <form className="card" onSubmit={submit}>
      <h2>{initial ? 'Edit participant' : 'New participant'}</h2>
      <ErrorBox error={error} />
      <div className="row">
        <div className="field"><label>First name<input value={f.firstName} onChange={set('firstName')} required /></label></div>
        <div className="field"><label>Middle<input value={f.middleName} onChange={set('middleName')} /></label></div>
        <div className="field"><label>Last name<input value={f.lastName} onChange={set('lastName')} required /></label></div>
      </div>
      <div className="row">
        <div className="field"><label>Date of birth<input type="date" value={f.dob} onChange={set('dob')} /></label></div>
        <div className="field"><label>Sex
          <select value={f.sex} onChange={set('sex')}><option value="">—</option><option value="M">Male</option><option value="F">Female</option></select></label></div>
        <div className="field"><label>Employee ID #<input value={f.employeeId} onChange={set('employeeId')} /></label></div>
        <div className="field"><label>Main organization
          <select value={f.departmentId} onChange={set('departmentId')}>
            <option value="">Unassigned</option>
            {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select></label></div>
      </div>
      <div className="row">
        <div className="field"><label>Email<input type="email" value={f.email} onChange={set('email')} /></label></div>
        <div className="field"><label>Phone<input value={f.phone} onChange={set('phone')} /></label></div>
        <div className="field"><label>Insurance ID<input value={f.insuranceId} onChange={set('insuranceId')} autoComplete="off"
          placeholder={initial?.insuranceLast4 ? `on file: ••••${initial.insuranceLast4} (type to replace)` : 'e.g. COHSG screenings'} /></label></div>
      </div>
      <div className="row">
        <div className="field" style={{ flex: '2 1 220px' }}><label>Mailing address<input value={f.street} onChange={set('street')} /></label></div>
        <div className="field"><label>City<input value={f.city} onChange={set('city')} /></label></div>
        <div className="field" style={{ flex: '0 1 70px' }}><label>State<input value={f.state} maxLength={20} onChange={set('state')} /></label></div>
        <div className="field" style={{ flex: '0 1 110px' }}><label>ZIP<input value={f.zip} onChange={set('zip')} /></label></div>
      </div>
      <div className="actions"><button>Save</button><button type="button" className="secondary" onClick={onCancel}>Cancel</button></div>
    </form>
  );
}

interface HistoryRow { id: string; assessedOn: string; status: string; source: string; engineVersion: string | null; departmentName: string | null; supersededBy?: string | null; correctsId?: string | null;
  results: { aerobic?: { vo2maxDisplayed: number; aerobicClass: string } | null; coronaryRisk?: { score: number; riskClass: string } | null;
    bodyComposition?: { fatPct: number } | null } | null }

export function ParticipantDetail() {
  const { id } = useParams();
  const { data, error, reload } = useData<ParticipantRow & { history: HistoryRow[]; organizations: Membership[] }>(`/api/participants/${id}`);
  const depts = useData<Department[]>('/api/departments');
  const [editing, setEditing] = useState(false);
  const [choosing, setChoosing] = useState(false);
  const nav = useNavigate();
  const { me } = useAuth();
  if (error) return <ErrorBox error={error} />;
  if (!data) return <p className="muted">Loading…</p>;
  const newAssessment = async (visitType: VisitType) => {
    const r = await post<{ id: string }>('/api/assessments', {
      participantId: data.id, assessedOn: localToday(), measurements: {}, visitType,
    });
    nav(`/assessments/${r.id}`);
  };
  return (
    <>
      <CoverSheetTopBar back={<Link to="/participants">← Participants</Link>} query={`participantId=${data.id}`} />
      {editing ? (
        <ParticipantForm departments={depts.data ?? []} initial={data} onCancel={() => setEditing(false)}
          onSaved={() => { setEditing(false); void reload(); }} />
      ) : (
        <div className="card">
          <div className="spread">
            <h1 style={{ margin: 0 }}>{data.lastName}, {data.firstName} {data.middleName ?? ''}</h1>
            <div className="actions"><button className="secondary" onClick={() => setEditing(true)}>Edit</button>
              <button onClick={() => setChoosing(!choosing)}>New assessment</button></div>
          </div>
          {choosing && <NewVisitChooser onPick={(t) => void newAssessment(t)} onCancel={() => setChoosing(false)} />}
          <p className="muted">
            {data.sex === 'M' ? 'Male' : data.sex === 'F' ? 'Female' : 'Sex not recorded'} · DOB {data.dob ?? '—'} ·
            ID # {data.employeeId ?? '—'} · {data.departmentName ?? 'No main organization'}
            {data.insuranceLast4 ? ` · Insurance ••••${data.insuranceLast4}` : ''}
          </p>
          {(data.email || data.phone || data.street || data.city) && <p className="muted small" style={{ marginTop: '-0.5rem' }}>
            {[data.email, data.phone, [data.street, data.city, [data.state, data.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ')].filter(Boolean).join(' · ')}</p>}
          {(!data.dob || !data.sex) && <div className="alert info">Date of birth and sex are needed before results can be calculated.</div>}
          {data.reviewFlags?.includes('walk_in_offline') && <div className="alert info">Added as a walk-in on an iPad and not yet checked against existing records. <Link to="/review">Review walk-ins</Link></div>}
        </div>
      )}
      {me && isAdmin(me.role) && <MergePerson from={data} onDone={(into) => nav(`/participants/${into}`)} />}
      <OrganizationsCard participantId={data.id} organizations={data.organizations} departments={depts.data ?? []} onChange={() => void reload()} />
      <div className="card">
        <h2>Assessment history</h2>
        {!data.history.length ? <p className="muted">No assessments yet.</p> : (
          <table>
            <thead><tr><th>Date</th><th>Status</th><th>Tested with</th><th>VO2max</th><th>Aerobic class</th><th>Risk score</th><th>Body fat</th><th>Source</th></tr></thead>
            <tbody>{data.history.map((h) => (
              <tr key={h.id} className="link" onClick={() => nav(`/assessments/${h.id}`)}>
                <td>{h.assessedOn}</td><td><span className={`badge ${h.status}`}>{h.status}</span>{h.supersededBy && <div className="muted small">replaced by a correction</div>}{h.correctsId && <div className="muted small">correction</div>}</td><td className="small">{h.departmentName ?? '—'}</td>
                <td>{h.results?.aerobic?.vo2maxDisplayed ?? '—'}</td><td>{h.results?.aerobic?.aerobicClass ?? '—'}</td>
                <td>{h.results?.coronaryRisk ? `${h.results.coronaryRisk.score} (${h.results.coronaryRisk.riskClass})` : '—'}</td>
                <td>{h.results?.bodyComposition ? `${h.results.bodyComposition.fatPct}%` : '—'}</td>
                <td className="muted small">{h.source === 'legacy_epl' ? 'ePhysioLogic' : 'NGF app'}</td>
              </tr>))}</tbody>
          </table>
        )}
      </div>
      <LabsCard participantId={data.id} />
      <DocumentsCard participantId={data.id} assessments={data.history} />
      <PersonPacketsCard participantId={data.id} />
      <ArchiveFilesCard participantId={data.id} />
      <ScanPilesCard participantId={data.id} />
    </>
  );
}

interface Membership { id: string; name: string; main: boolean; added: boolean; tested: boolean; archived: boolean }

/** Every organization the person belongs to: main, tested with (from their assessments), and added by hand or roster. */
function OrganizationsCard({ participantId, organizations, departments, onChange }: {
  participantId: string; organizations: Membership[]; departments: Department[]; onChange: () => void;
}) {
  const [adding, setAdding] = useState('');
  const [error, setError] = useState<unknown>(null);
  const add = async () => {
    if (!adding) return;
    setError(null);
    try { await post(`/api/participants/${participantId}/organizations`, { departmentId: adding }); setAdding(''); onChange(); } catch (e) { setError(e); }
  };
  const remove = async (m: Membership) => {
    if (!confirm(`Remove ${m.name} from this person's organizations?`)) return;
    setError(null);
    try { await api('DELETE', `/api/participants/${participantId}/organizations/${m.id}`); onChange(); } catch (e) { setError(e); }
  };
  const have = new Set(organizations.map((o) => o.id));
  return (
    <div className="card">
      <h2>Organizations</h2>
      <p className="muted small">A person can belong to more than one organization. Each assessment is tagged with the organization they tested with.</p>
      <ErrorBox error={error} />
      {!organizations.length ? <p className="muted">No organization yet.</p> : (
        <table>
          <tbody>{organizations.map((m) => (
            <tr key={m.id}>
              <td>{m.name}{m.archived && <span className="muted small"> (archived)</span>}</td>
              <td className="small">
                {m.main && <span className="badge">main</span>}{' '}
                {m.tested && <span className="badge">tested with</span>}{' '}
                {m.added && !m.main && <span className="badge">added</span>}
              </td>
              <td style={{ textAlign: 'right' }}>
                {m.added && !m.main && !m.tested && <button className="secondary small" onClick={() => void remove(m)}>Remove</button>}
              </td>
            </tr>))}</tbody>
        </table>
      )}
      <div className="row" style={{ marginTop: 8, alignItems: 'end' }}>
        <div className="field"><label>Add an organization
          <select value={adding} onChange={(e) => setAdding(e.target.value)}>
            <option value="">Choose…</option>
            {departments.filter((d) => !have.has(d.id)).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select></label></div>
        <div className="field"><button type="button" disabled={!adding} onClick={() => void add()}>Add</button></div>
      </div>
    </div>
  );
}

interface LabValue {
  code: string; name: string; valueNum: number | null; valueText: string | null; unit: string | null; refLow: number | null; refHigh: number | null;
  refText: string | null; flag: string | null; prevNum: number | null; prevText: string | null; prevOn: string | null;
}
interface LabReport {
  id: string; serviceName: string; performedOn: string; labName: string | null; accession: string | null; assessmentId: string | null;
  documentId: string | null; checked: boolean; fasting: boolean | null; results: LabValue[];
}
const labVal = (n: number | null, t: string | null) => t ?? (n === null ? '—' : String(n));
const labRef = (v: LabValue) => v.refText ?? (v.refLow !== null && v.refHigh !== null ? `${v.refLow}–${v.refHigh}` : v.refLow !== null ? `>${v.refLow}` : v.refHigh !== null ? `<${v.refHigh}` : '');

/** Lab results (from LabCorp reports), newest first; each value with its flag and the previous result. */
function LabsCard({ participantId }: { participantId: string }) {
  const { data, error, reload } = useData<LabReport[]>(`/api/participants/${participantId}/labs`);
  const [open, setOpen] = useState<string | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const [adding, setAdding] = useState(false);
  if (!data?.length && !error) return adding
    ? <div className="card"><h2>Lab results</h2><AddLabs participantId={participantId} onDone={() => { setAdding(false); void reload(); }} onCancel={() => setAdding(false)} /></div>
    : <p className="no-print small" style={{ margin: '0 0 0.5rem' }}><button className="link" onClick={() => setAdding(true)}>Add lab results (e.g. heavy metals)…</button></p>;
  const check = async (id: string) => {
    setErr(null);
    try { await post(`/api/test-records/${id}/check`); await reload(); } catch (x) { setErr(x); }
  };
  const shown = open ?? data?.[0]?.id ?? null;
  return (
    <div className="card">
      <h2>Lab results{!adding && <button className="link small no-print" style={{ marginLeft: 12, fontWeight: 'normal' }} onClick={() => setAdding(true)}>Add results…</button>}</h2>
      {adding && <AddLabs participantId={participantId} onDone={() => { setAdding(false); void reload(); }} onCancel={() => setAdding(false)} />}
      <ErrorBox error={error ?? err} />
      <div className="lab-reports">{data?.map((r) => {
        const flags = r.results.filter((v) => v.flag).length;
        return (
          <div key={r.id} className={`lab-report${shown === r.id ? ' open' : ''}`}>
            <button type="button" className="lab-report-head" onClick={() => setOpen(shown === r.id ? '' : r.id)}>
              <b>{r.performedOn}</b> · {r.labName ?? 'Lab'} · {r.serviceName} · {r.results.length} values
              {flags > 0 && <span className="badge void" style={{ marginLeft: 6 }}>{flags} flagged</span>}
              {!r.checked && <span className="badge provisional" style={{ marginLeft: 6 }}>not yet checked</span>}
            </button>
            {shown === r.id && (
              <div className="lab-report-body">
                <p className="muted small" style={{ margin: '0.25rem 0' }}>
                  {r.accession ? `Accession ${r.accession} · ` : ''}{r.fasting === true ? 'Fasting · ' : r.fasting === false ? 'Not fasting · ' : ''}
                  {r.documentId && <a href={`/api/documents/${r.documentId}/file`} target="_blank" rel="noopener">Open the lab report</a>}
                  {r.assessmentId && <> · <Link to={`/assessments/${r.assessmentId}`}>Visit</Link></>}
                  {!r.checked && <> · <button className="link" onClick={() => void check(r.id)}>Mark checked against the report</button></>}
                </p>
                <table className="lab-table">
                  <thead><tr><th>Test</th><th>Result</th><th>Flag</th><th>Units</th><th>Reference</th><th>Previous</th></tr></thead>
                  <tbody>{r.results.map((v) => (
                    <tr key={v.code} className={v.flag ? 'lab-flag' : ''}>
                      <td>{v.name}</td><td className="num">{labVal(v.valueNum, v.valueText)}</td><td>{v.flag ?? ''}</td>
                      <td className="small">{v.unit ?? ''}</td><td className="small">{labRef(v)}</td>
                      <td className="small muted">{v.prevOn ? `${labVal(v.prevNum, v.prevText)} (${v.prevOn})` : ''}</td>
                    </tr>))}</tbody>
                </table>
              </div>)}
          </div>);
      })}</div>
    </div>
  );
}

interface LabTestGroups { groups: { service: string; name: string; tests: { code: string; name: string }[] }[] }

/** Enter lab results by hand: heavy metals by default (the hand-written Haz-Mat sheets), or any blood test. */
function AddLabs({ participantId, onDone, onCancel }: { participantId: string; onDone: () => void; onCancel: () => void }) {
  const { data } = useData<LabTestGroups>('/api/lab-tests');
  const [group, setGroup] = useState('heavy_metals');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [labName, setLabName] = useState('');
  const [vals, setVals] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const tests = data?.groups.find((g) => g.service === group)?.tests ?? [];
  const filled = Object.entries(vals).filter(([code, v]) => v.trim() && tests.some((t) => t.code === code));
  const save = async () => {
    setErr(null); setBusy(true);
    try {
      await post(`/api/participants/${participantId}/labs`, { performedOn: date, labName: labName.trim() || null,
        values: filled.map(([code, value]) => ({ code, value: value.trim() })) });
      onDone();
    } catch (x) { setErr(x); } finally { setBusy(false); }
  };
  return (
    <div className="card move-doc">
      <b>Add lab results</b>
      <p className="muted small" style={{ margin: '0.25rem 0' }}>Type each value as written on the lab sheet ("&lt;1.0" is kept as written). Leave blanks for tests not done.</p>
      <ErrorBox error={err} />
      <div className="row">
        <div className="field"><label htmlFor="al-g">Tests</label>
          <select id="al-g" value={group} onChange={(e) => setGroup(e.target.value)}>
            {data?.groups.map((g) => <option key={g.service} value={g.service}>{g.name}</option>)}</select></div>
        <div className="field"><label htmlFor="al-d">Test date</label><input id="al-d" type="date" value={date} onChange={(e) => setDate(e.target.value)} /></div>
        <div className="field"><label htmlFor="al-l">Lab (optional)</label><input id="al-l" value={labName} onChange={(e) => setLabName(e.target.value)} /></div>
      </div>
      <div className="row" style={{ flexWrap: 'wrap' }}>{tests.map((t) => (
        <div key={t.code} className="field" style={{ flex: '0 0 9rem' }}><label htmlFor={`al-${t.code}`}>{t.name}</label>
          <input id={`al-${t.code}`} value={vals[t.code] ?? ''} onChange={(e) => setVals({ ...vals, [t.code]: e.target.value })} /></div>))}</div>
      <div className="actions"><button disabled={busy || !filled.length || !date} onClick={() => void save()}>Save {filled.length || ''} result{filled.length === 1 ? '' : 's'}</button>
        <button className="secondary" onClick={onCancel}>Cancel</button></div>
    </div>
  );
}

interface FoundPerson { id: string; firstName: string; lastName: string; dob: string | null; employeeId: string | null; departmentName: string | null; lastAssessedOn?: string | null }

/** Admins: this record is a duplicate of another person; move everything to the other record and retire this one. */
function MergePerson({ from, onDone }: { from: ParticipantRow; onDone: (intoId: string) => void }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState(`${from.firstName} ${from.lastName}`);
  const [found, setFound] = useState<FoundPerson[]>([]);
  const [into, setInto] = useState('');
  const [reason, setReason] = useState('Same person: same name and date of birth');
  const [err, setErr] = useState<unknown>(null);
  const search = async () => {
    setErr(null);
    try { setFound((await get<FoundPerson[]>(`/api/participants?limit=20&q=${encodeURIComponent(q.trim())}`)).filter((p) => p.id !== from.id)); } catch (x) { setErr(x); }
  };
  const merge = async () => {
    const other = found.find((p) => p.id === into);
    if (!other) return;
    if (!window.confirm(`Merge this record (${from.lastName}, ${from.firstName}, ID ${from.employeeId ?? '—'}) into ${other.lastName}, ${other.firstName} (ID ${other.employeeId ?? '—'})?

Everything on this record moves to that one, and this record is retired. It can be undone.`)) return;
    setErr(null);
    try { await post(`/api/participants/${from.id}/merge`, { intoId: into, reason: reason.trim() }); onDone(into); } catch (x) { setErr(x); }
  };
  if (!open) return <p className="no-print small" style={{ textAlign: 'right', margin: '-0.5rem 0 0.5rem' }}>
    <button className="link" onClick={() => { setOpen(true); void search(); }}>This is a duplicate: merge into another record…</button></p>;
  return (
    <div className="card move-doc">
      <b>Merge this record into another person</b>
      <p className="muted small" style={{ margin: '0.25rem 0' }}>For the same person on file twice (for example imported from ePhysioLogic under two ID numbers).
        Assessments, documents and organizations move to the record you pick; this record is retired. Undo is possible.</p>
      <ErrorBox error={err} />
      <div className="row" style={{ alignItems: 'end' }}>
        <div className="field"><label htmlFor="mp-q">Find the record to keep</label>
          <input id="mp-q" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void search(); } }} /></div>
        <div style={{ flex: '0 0 auto' }}><button type="button" className="secondary" onClick={() => void search()}>Search</button></div>
      </div>
      {found.map((p) => (
        <label key={p.id} className="small" style={{ display: 'block' }}>
          <input type="radio" checked={into === p.id} onChange={() => setInto(p.id)} /> <strong>{p.lastName}, {p.firstName}</strong>
          {' '}· DOB {p.dob ?? '—'} · ID {p.employeeId ?? '—'} · {p.departmentName ?? 'No organization'}{p.lastAssessedOn ? ` · last tested ${p.lastAssessedOn}` : ''}
          {p.dob && p.dob === from.dob && <span className="badge provisional" style={{ marginLeft: 6 }}>same DOB</span>}
        </label>))}
      {!found.length && <p className="muted small">No other records found.</p>}
      <div className="field" style={{ marginTop: 6 }}><label htmlFor="mp-r">Reason</label><input id="mp-r" value={reason} onChange={(e) => setReason(e.target.value)} /></div>
      <div className="actions"><button disabled={!into || reason.trim().length < 3} onClick={() => void merge()}>Merge into the selected record</button>
        <button className="secondary" onClick={() => setOpen(false)}>Cancel</button></div>
    </div>
  );
}

